import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { authApi, getErrorMessage, userApi } from "@/lib/api";
import type { ChangePasswordRequest } from "@charisol-spark/client-core";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Alert } from "@/components/ui/Alert";
import { AuthenticatorEnrollmentFlow } from "@/components/auth/AuthenticatorEnrollmentFlow";

/**
 * Security settings page — where admins enrol in authenticator-app (TOTP)
 * MFA (#163). Copied from packages/frontend/src/pages/settings/SecurityPage.tsx
 * unchanged in behaviour, wired to @/lib/api instead of the frontend's
 * services/*.
 *
 * First-access admins no longer reach this page to enrol — AdminMfaGate's
 * full-screen forced setup runs before any admin page renders (see
 * docs/runbooks/admin-mfa.md). This page still covers checking status
 * voluntarily and changing password; the enrolment form itself
 * (AuthenticatorEnrollmentFlow) is shared with that forced gate.
 */
export function SecurityPage() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [clientError, setClientError] = useState("");

  const changePasswordMutation = useMutation({
    mutationFn: (data: ChangePasswordRequest) => userApi.changePassword(data),
    onSuccess: () => {
      setSuccessMsg("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setClientError("");
      setTimeout(() => setSuccessMsg(""), 5000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg("");
    setClientError("");

    if (newPassword !== confirmPassword) {
      setClientError("New passwords do not match.");
      return;
    }
    if (currentPassword === newPassword) {
      setClientError("New password must be different from current password.");
      return;
    }

    changePasswordMutation.mutate({ currentPassword, newPassword });
  };

  const isValid =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    confirmPassword.length > 0;

  return (
    <div className="w-full space-y-8">
      <section className="rounded-xl border border-border/50 bg-card/50 p-6">
        <h2 className="text-lg font-semibold tracking-tight mb-2">
          Change Password
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          Your password must be at least 8 characters and include uppercase,
          lowercase, and a number.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Current Password</Label>
            <Input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              autoComplete="current-password"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
            {newPassword.length > 0 && (
              <ul className="space-y-0.5 text-xs mt-2">
                <PasswordRule
                  met={newPassword.length >= 8}
                  text="At least 8 characters"
                />
                <PasswordRule
                  met={/[A-Z]/.test(newPassword)}
                  text="One uppercase letter"
                />
                <PasswordRule
                  met={/[a-z]/.test(newPassword)}
                  text="One lowercase letter"
                />
                <PasswordRule
                  met={/[0-9]/.test(newPassword)}
                  text="One number"
                />
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              autoComplete="new-password"
              error={
                confirmPassword.length > 0 && confirmPassword !== newPassword
                  ? "Passwords do not match"
                  : undefined
              }
            />
          </div>

          {clientError && <Alert variant="error">{clientError}</Alert>}
          {changePasswordMutation.isError && (
            <Alert variant="error">
              {getErrorMessage(changePasswordMutation.error)}
            </Alert>
          )}
          {successMsg && <Alert variant="success">{successMsg}</Alert>}

          <div className="flex items-center justify-end pt-2">
            <Button
              type="submit"
              disabled={!isValid}
              loading={changePasswordMutation.isPending}
            >
              Update Password
            </Button>
          </div>
        </form>
      </section>

      <AuthenticatorAppSection />
    </div>
  );
}

/**
 * Authenticator-app (TOTP) enrolment status + the shared enrol/verify form
 * (AuthenticatorEnrollmentFlow) for an admin who wants to set one up — or
 * replace one — voluntarily from here rather than through the forced
 * first-access gate.
 */
function AuthenticatorAppSection() {
  const statusQuery = useQuery({
    queryKey: ["auth", "mfa", "status"],
    queryFn: () => authApi.getMfaStatus(),
  });

  const enrolled = statusQuery.data?.enrolled ?? false;

  return (
    <section className="rounded-xl border border-border/50 bg-card/50 p-6">
      <h2 className="text-lg font-semibold tracking-tight mb-2">
        Authenticator app
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        Add a second factor with an authenticator app (Google Authenticator,
        1Password, Authy, or similar). Anyone signing in still needs your
        password first.
      </p>

      {statusQuery.isLoading && (
        <p className="text-sm text-muted-foreground">Checking status…</p>
      )}

      {!statusQuery.isLoading && enrolled && (
        <Alert variant="success">
          Two-factor authentication is enabled on your account. To replace
          it with a new device, sign out and sign in again with your current
          authenticator code, then come back here — replacing an existing
          factor requires proving you still have it (see
          docs/runbooks/admin-mfa.md if you've lost access to it instead).
        </Alert>
      )}

      {!statusQuery.isLoading && !enrolled && (
        <AuthenticatorEnrollmentFlow
          onEnrolled={() => statusQuery.refetch()}
        />
      )}
    </section>
  );
}

function PasswordRule({ met, text }: { met: boolean; text: string }) {
  return (
    <li className={met ? "text-success" : "text-muted-foreground"}>
      <span className="mr-1.5">{met ? "✓" : "○"}</span>
      {text}
    </li>
  );
}
